import { z } from 'zod';

export const COMPONENT_STATE_API_VERSION = 'v1';
export const COMPONENT_STATE_API_PREFIX = `/state/${COMPONENT_STATE_API_VERSION}`;
export const COMPONENT_STATE_CAPABILITY_HEADER = 'x-mutiny-state-capability';
export const COMPONENT_STATE_VISITOR_HEADER = 'x-mutiny-visitor-id';

export const ComponentStateCapabilitySchema = z.string().uuid();
export const ComponentStateCollectionNameSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);
export const ComponentStateVersionSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu);
export const ComponentStateDocumentIdSchema = ComponentStateVersionSchema;
export const ComponentStateEmailSchema = z.string().trim().toLowerCase().email();
export const ComponentStateIdentitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('anonymous'), visitorId: z.string().uuid(), email: z.null() }),
  z.object({ kind: z.literal('identified'), visitorId: z.string().uuid(), email: ComponentStateEmailSchema }),
]);

export type ComponentStateValue =
  | string
  | number
  | boolean
  | null
  | ComponentStateValue[]
  | { [key: string]: ComponentStateValue };

export const ComponentStateValueSchema: z.ZodType<ComponentStateValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(ComponentStateValueSchema),
    z.record(z.string(), ComponentStateValueSchema),
  ]),
);

export const ComponentStateDataSchema = z.record(z.string(), ComponentStateValueSchema);

export const ComponentStateDocumentSchema = z.object({
  id: ComponentStateDocumentIdSchema,
  data: ComponentStateDataSchema,
  writer: ComponentStateIdentitySchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  version: ComponentStateVersionSchema,
});

export const ComponentStateSortFieldSchema = z.enum(['createdAt', 'updatedAt']);
export const ComponentStateSortDirectionSchema = z.enum(['asc', 'desc']);
export const ComponentStateQuerySchema = z
  .object({
    orderBy: ComponentStateSortFieldSchema.default('createdAt'),
    direction: ComponentStateSortDirectionSchema.default('asc'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().min(1).optional(),
    whereField: z
      .string()
      .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/u)
      .optional(),
    whereEquals: z.string().optional(),
  })
  .refine((query) => Boolean(query.whereField) === (query.whereEquals !== undefined));
export const ComponentStateListResponseSchema = z.object({
  docs: z.array(ComponentStateDocumentSchema),
  nextCursor: z.string().nullable(),
});
export const ComponentStateInsertRequestSchema = z.object({
  id: ComponentStateDocumentIdSchema,
  data: ComponentStateDataSchema,
});
export const ComponentStateReplaceRequestSchema = z.object({ data: ComponentStateDataSchema });
export const ComponentStateMutationRequestSchema = z.discriminatedUnion('operation', [
  z.object({
    operation: z.literal('merge'),
    data: ComponentStateDataSchema,
    expectedVersion: ComponentStateVersionSchema.optional(),
  }),
  z.object({
    operation: z.literal('increment'),
    field: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/u),
    amount: z.number().finite(),
    expectedVersion: ComponentStateVersionSchema.optional(),
  }),
  z.object({
    operation: z.literal('toggle'),
    field: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/u),
    expectedVersion: ComponentStateVersionSchema.optional(),
  }),
  z.object({
    operation: z.literal('append'),
    field: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/u),
    value: ComponentStateValueSchema,
    expectedVersion: ComponentStateVersionSchema.optional(),
  }),
]);

export const ComponentStateErrorCodeSchema = z.enum([
  'unauthorized',
  'invalid_request',
  'payload_too_large',
  'quota_exceeded',
  'not_found',
  'conflict',
  'invalid_operation',
  'server_error',
]);
export const ComponentStateErrorResponseSchema = z.object({
  error: z.object({ code: ComponentStateErrorCodeSchema }),
});

export type ComponentStateData = z.infer<typeof ComponentStateDataSchema>;
export type ComponentStateDocument = z.infer<typeof ComponentStateDocumentSchema>;
export type ComponentStateErrorCode = z.infer<typeof ComponentStateErrorCodeSchema>;
export type ComponentStateIdentity = z.infer<typeof ComponentStateIdentitySchema>;
export type ComponentStateMutationRequest = z.infer<typeof ComponentStateMutationRequestSchema>;
export type ComponentStateQuery = z.input<typeof ComponentStateQuerySchema>;
export type ResolvedComponentStateQuery = z.output<typeof ComponentStateQuerySchema>;

export function buildComponentStateIdentity(visitorId: string, email: string | null): ComponentStateIdentity {
  return email === null ? { kind: 'anonymous', visitorId, email: null } : { kind: 'identified', visitorId, email };
}

export const componentStateCollectionPath = (collection: string, query?: ComponentStateQuery): string => {
  const path = `${COMPONENT_STATE_API_PREFIX}/collections/${encodeURIComponent(collection)}`;
  if (!query) return path;
  const params = new URLSearchParams();
  if (query.orderBy) params.set('orderBy', query.orderBy);
  if (query.direction) params.set('direction', query.direction);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.whereField) params.set('whereField', query.whereField);
  if (query.whereEquals !== undefined) params.set('whereEquals', query.whereEquals);
  const search = params.toString();
  return search ? `${path}?${search}` : path;
};

export const componentStateDocumentPath = (collection: string, id: string): string =>
  `${componentStateCollectionPath(collection)}/${encodeURIComponent(id)}`;
